import Web3 from 'web3';
import { AbiItem } from 'web3-utils';

// ERC-20 balance ABI
const ERC20_BALANCE_ABI: AbiItem[] = [
  {
    constant: true,
    inputs: [{ name: 'owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
];

/**
 * Get ERC-20 token balance for an address
 */
export const getERC20Balance = async (
  web3: Web3,
  tokenAddress: string,
  ownerAddress: string,
): Promise<string> => {
  const tokenContract = new web3.eth.Contract(ERC20_BALANCE_ABI, tokenAddress);
  const balance = await tokenContract.methods.balanceOf(ownerAddress).call();
  return balance.toString();
};

/**
 * Get native balance for an address
 */
export const getNativeBalance = async (web3: Web3, address: string): Promise<string> => {
  const balance = await web3.eth.getBalance(address);
  return balance.toString();
};

/**
 * Format balance with comma separators for thousands
 */
export const displayBalance = (balance: string | number): string => {
  const numBalance = typeof balance === 'string' ? parseFloat(balance) : balance;

  if (isNaN(numBalance)) {
    throw new Error(`Invalid balance: ${balance}`);
  }

  if (numBalance === 0) {
    return '0';
  }

  const isWholeNumber = numBalance % 1 === 0;

  if (isWholeNumber) {
    // No decimals for whole numbers
    // 1,000 -> 1,000
    return numBalance.toLocaleString('en-US', {
      maximumFractionDigits: 0,
    });
  } else if (numBalance < 0.0001) {
    // For very small numbers, show up to 8 significant digits without scientific notation
    // 0.000001234567 -> 0.00000123
    // Find the number of leading zeros after decimal point
    const str = numBalance.toFixed(20); // Get enough precision
    const match = str.match(/^0\.0*[1-9]/);
    if (match) {
      const leadingZeros = match[0].length - 2; // subtract "0."
      const significantDigits = 6; // show 6 significant digits
      const totalDecimals = leadingZeros + significantDigits;
      return numBalance.toFixed(Math.min(totalDecimals, 18)); // cap at 18 decimals
    }
    return numBalance.toFixed(8);
  } else {
    // Exactly 4 decimals for fractional numbers >= 0.0001
    // 1,000.25 -> 1,000.2500
    return numBalance.toLocaleString('en-US', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  }
};

/**
 * Format gas fees with appropriate unit (ETH/EWT or GWEI)
 * For values >= 0.001: show in ETH/EWT with token symbol
 * For values < 0.001: show in GWEI
 */
export const formatGasFee = (
  feeInEther: string | number,
  tokenSymbol: 'ETH' | 'EWT' = 'ETH',
): { value: string; unit: string } => {
  const numFee = typeof feeInEther === 'string' ? parseFloat(feeInEther) : feeInEther;

  if (isNaN(numFee) || numFee === 0) {
    return { value: '0', unit: tokenSymbol };
  }

  // Threshold: 0.001 ETH/EWT = 1,000,000 GWEI
  if (numFee < 0.001) {
    // Convert to GWEI (1 ETH = 1,000,000,000 GWEI)
    const gwei = numFee * 1_000_000_000;

    // Format GWEI with appropriate decimals (no trailing zeros)
    if (gwei < 1) {
      // Show up to 4 decimals for sub-GWEI values, remove trailing zeros
      return { value: parseFloat(gwei.toFixed(4)).toString(), unit: 'GWEI' };
    } else if (gwei < 1000) {
      // Show up to 2 decimals for small GWEI values, remove trailing zeros
      return { value: parseFloat(gwei.toFixed(2)).toString(), unit: 'GWEI' };
    } else {
      // Show as whole number for larger GWEI values
      return { value: Math.round(gwei).toLocaleString('en-US'), unit: 'GWEI' };
    }
  } else {
    // Show in ETH/EWT, remove trailing zeros
    const formatted = numFee.toFixed(4);
    return {
      value: parseFloat(formatted).toString(),
      unit: tokenSymbol,
    };
  }
};
